import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckSquare,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Inbox,
  Loader2,
  RefreshCcw,
  Search,
  Send,
  Timer,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RightDockDetailSheet } from '@/components/major-list';

type WorkItemState = 'draft' | 'pending_review' | 'approved' | 'committed' | 'rejected' | 'expired';
type WorkItemFilter = 'all' | 'action_needed' | 'warehouse' | 'tm' | 'cf' | 'snoozed' | 'committed';

interface WorkItemRow {
  id: string;
  tenant_id: string;
  entity_type: string;
  entity_id: string | null;
  source_type: string;
  source_ref: string | null;
  state: WorkItemState;
  priority: 'P0' | 'P1' | 'P2';
  proposed_payload: unknown;
  raw_extraction: unknown;
  evidence: unknown;
  validation_issues: unknown;
  confidence_summary: unknown;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  committed_entity_id?: string | null;
  attachments?: Array<Record<string, unknown>>;
}

const STATE_LABEL: Record<WorkItemState, string> = {
  draft: 'Draft',
  pending_review: 'Review',
  approved: 'Approved',
  committed: 'Posted',
  rejected: 'Rejected',
  expired: 'Expired',
};

const STATE_CLASS: Record<WorkItemState, string> = {
  draft: 'border-slate-300 bg-slate-50 text-slate-700',
  pending_review: 'border-amber-300 bg-amber-50 text-amber-700',
  approved: 'border-blue-300 bg-blue-50 text-blue-700',
  committed: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  rejected: 'border-red-300 bg-red-50 text-red-700',
  expired: 'border-zinc-300 bg-zinc-50 text-zinc-700',
};

function asRecord(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function asArray(value: unknown): Array<Record<string, unknown>> {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) ? parsed.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object') : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object') : [];
}

function text(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function moduleFromWorkItem(item: WorkItemRow): string {
  const payload = asRecord(item.proposed_payload);
  const evidence = asRecord(item.evidence);
  const evidencePack = asRecord(evidence.evidencePack);
  const domain = text(asRecord(asRecord(item.raw_extraction).metadata).domain || asRecord(item.confidence_summary).domain);
  const profile = text(asRecord(item.confidence_summary).profile || evidencePack.profile);
  const scope = text(payload.workScope || payload.work_scope);
  if (item.entity_type.includes('warehouse') || domain === 'warehouse' || profile.includes('wm') || scope.startsWith('wm.')) return 'WM';
  if (profile.includes('cf') || scope.startsWith('cf.')) return 'CF';
  if (profile.includes('tm') || scope.startsWith('tm.')) return 'TM';
  if (profile.includes('ct') || scope.startsWith('ct.')) return 'CT';
  return text(item.entity_type || 'GENERAL').toUpperCase();
}

function workItemTitle(item: WorkItemRow): string {
  const payload = asRecord(item.proposed_payload);
  return text(payload.summary || payload.actionType || payload.intent || item.source_ref || item.entity_type) || 'Work item';
}

function entityRouteSlug(entityType: string): string {
  const normalized = entityType.replace(/_/g, '-').toLowerCase();
  if (['warehouse-activity', 'warehouse-activities', 'warehouse-task', 'warehouse-tasks'].includes(normalized)) return 'warehouse-tasks';
  if (normalized === 'job') return 'jobs';
  if (normalized === 'shipment') return 'shipments';
  if (normalized === 'customer') return 'customers';
  return normalized;
}

function formatAge(value: string): string {
  const ms = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(ms / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function compactJson(value: unknown): string {
  return JSON.stringify(asRecord(value), null, 2);
}

function queueInfo(item: WorkItemRow): Record<string, unknown> {
  return asRecord(asRecord(item.proposed_payload)._queue);
}

function isSnoozed(item: WorkItemRow): boolean {
  const snoozedUntil = text(queueInfo(item).snoozedUntil);
  return !!snoozedUntil && new Date(snoozedUntil).getTime() > Date.now();
}

function dataFieldRows(item: WorkItemRow): Array<{ field: string; value: string; source: string; confidence: string }> {
  const payload = asRecord(item.proposed_payload);
  const confidence = asRecord(asRecord(item.confidence_summary).fields);
  return Object.entries(payload)
    .filter(([key]) => key !== '_queue')
    .slice(0, 30)
    .map(([field, value]) => ({
      field,
      value: typeof value === 'object' ? JSON.stringify(value) : text(value || '-'),
      source: text(asRecord(item.evidence)[field] || asRecord(item.raw_extraction)[field] || item.source_ref || '-'),
      confidence: typeof confidence[field] === 'number' ? `${Math.round(Number(confidence[field]) * 100)}%` : '-',
    }));
}

export default function WorkItemsReviewPage() {
  const [items, setItems] = useState<WorkItemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<WorkItemFilter>('action_needed');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [detail, setDetail] = useState<WorkItemRow | null>(null);
  const [payloadText, setPayloadText] = useState('');
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [bulkAssignee, setBulkAssignee] = useState('');

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<{ items: WorkItemRow[]; total: number }>('/work-items', { limit: 200 });
      setItems(data.items ?? []);
      setSelectedIds((current) => current.filter((id) => (data.items ?? []).some((item) => item.id === id)));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load work items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    setSelectedId(id);
    try {
      const row = await api.get<WorkItemRow>(`/work-items/${id}`);
      setDetail(row);
      setPayloadText(compactJson(row.proposed_payload));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load work item');
      setDetail(null);
    }
  }, []);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const module = moduleFromWorkItem(item);
      const title = workItemTitle(item);
      const snoozed = isSnoozed(item);
      const matchesFilter =
        filter === 'all'
        || (filter === 'action_needed' && !snoozed && ['draft', 'pending_review', 'approved'].includes(item.state))
        || (filter === 'warehouse' && ['WM', 'CF'].includes(module))
        || (filter === 'tm' && module === 'TM')
        || (filter === 'cf' && module === 'CF')
        || (filter === 'snoozed' && snoozed)
        || (filter === 'committed' && item.state === 'committed');
      if (!matchesFilter) return false;
      if (!query) return true;
      return [title, item.entity_type, item.source_type, item.source_ref, module]
        .some((value) => text(value).toLowerCase().includes(query));
    });
  }, [filter, items, search]);

  const counts = useMemo(() => ({
    actionNeeded: items.filter((item) => !isSnoozed(item) && ['draft', 'pending_review', 'approved'].includes(item.state)).length,
    warehouse: items.filter((item) => ['WM', 'CF'].includes(moduleFromWorkItem(item))).length,
    cf: items.filter((item) => moduleFromWorkItem(item) === 'CF').length,
    snoozed: items.filter(isSnoozed).length,
    committed: items.filter((item) => item.state === 'committed').length,
  }), [items]);

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((itemId) => itemId !== id) : [...current, id]);
  };

  const toggleVisibleSelection = () => {
    const visibleIds = visibleItems.map((item) => item.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds((current) => allSelected ? current.filter((id) => !visibleIds.includes(id)) : Array.from(new Set([...current, ...visibleIds])));
  };

  const runBulkAction = async (action: 'assign' | 'snooze' | 'submit' | 'approve' | 'reject' | 'advance') => {
    if (selectedIds.length === 0) return;
    setBusyAction(`bulk-${action}`);
    try {
      const body: Record<string, unknown> = { ids: selectedIds, action };
      if (action === 'assign') body.assignedTo = bulkAssignee.trim() || null;
      if (action === 'snooze') body.snoozedUntil = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString();
      if (action === 'reject') body.reason = 'Rejected from AI work queue bulk action';
      const result = await api.post<{ success: number; failed: number }>('/work-items/bulk', body);
      toast.success(`Bulk ${action}: ${result.success} done, ${result.failed} failed`);
      await loadItems();
      if (detail) await loadDetail(detail.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Bulk action failed');
    } finally {
      setBusyAction(null);
    }
  };

  const runAction = async (action: 'save' | 'submit' | 'approve' | 'reject' | 'commit' | 'advance') => {
    if (!detail) return;
    setBusyAction(action);
    try {
      if (action === 'save') {
        await api.patch(`/work-items/${detail.id}`, { proposedPayload: JSON.parse(payloadText) });
        toast.success('Draft saved');
      }
      if (action === 'submit') await api.post(`/work-items/${detail.id}/submit`, { createApproval: false });
      if (action === 'approve') await api.post(`/work-items/${detail.id}/approve`, {});
      if (action === 'reject') await api.post(`/work-items/${detail.id}/reject`, { reason: 'Rejected from Work Items Review' });
      if (action === 'commit') await api.post(`/work-items/${detail.id}/commit`, {});
      if (action === 'advance') {
        if (detail.state === 'draft') await api.post(`/work-items/${detail.id}/submit`, { createApproval: false });
        if (detail.state === 'draft' || detail.state === 'pending_review') await api.post(`/work-items/${detail.id}/approve`, {});
        await api.post(`/work-items/${detail.id}/commit`, {});
      }
      if (action !== 'save') toast.success('Work item updated');
      await loadItems();
      await loadDetail(detail.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Action failed');
    } finally {
      setBusyAction(null);
    }
  };

  const tabs: Array<{ id: WorkItemFilter; label: string; count: number }> = [
    { id: 'action_needed', label: 'Action Needed', count: counts.actionNeeded },
    { id: 'warehouse', label: 'WM / CF', count: counts.warehouse },
    { id: 'tm', label: 'TM', count: items.filter((item) => moduleFromWorkItem(item) === 'TM').length },
    { id: 'cf', label: 'CF', count: counts.cf },
    { id: 'snoozed', label: 'Snoozed', count: counts.snoozed },
    { id: 'committed', label: 'Posted', count: counts.committed },
    { id: 'all', label: 'All', count: items.length },
  ];

  return (
    <div className="flex min-h-full flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <ClipboardCheck className="h-5 w-5 text-slate-700" />
            Work Items Review
          </h1>
          <p className="text-sm text-muted-foreground">AI intake、PWA capture、blind receiving、document review 的统一审核队列。</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadItems()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-b">
        <div className="flex flex-wrap gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setFilter(tab.id)}
              className={cn(
                'border-b-2 px-3 py-2 text-sm font-medium',
                filter === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label} <span className="text-xs text-muted-foreground">({tab.count})</span>
            </button>
          ))}
        </div>
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="w-72 pl-8" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search source, entity, summary" />
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-slate-50 px-3 py-2 text-sm">
          <div className="font-medium">{selectedIds.length} selected</div>
          <Input
            className="h-8 w-72 bg-background"
            value={bulkAssignee}
            onChange={(event) => setBulkAssignee(event.target.value)}
            placeholder="Assignee user UUID for assign"
          />
          <Button size="sm" variant="outline" onClick={() => void runBulkAction('assign')} disabled={!!busyAction}>
            <UserCheck className="mr-1 h-3.5 w-3.5" /> Assign
          </Button>
          <Button size="sm" variant="outline" onClick={() => void runBulkAction('snooze')} disabled={!!busyAction}>
            <Timer className="mr-1 h-3.5 w-3.5" /> Snooze 4h
          </Button>
          <Button size="sm" variant="outline" onClick={() => void runBulkAction('submit')} disabled={!!busyAction}>
            <Send className="mr-1 h-3.5 w-3.5" /> Submit
          </Button>
          <Button size="sm" variant="outline" onClick={() => void runBulkAction('approve')} disabled={!!busyAction}>
            <CheckSquare className="mr-1 h-3.5 w-3.5" /> Approve
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => void runBulkAction('advance')} disabled={!!busyAction}>
            <ClipboardCheck className="mr-1 h-3.5 w-3.5" /> Review + Post
          </Button>
          <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => void runBulkAction('reject')} disabled={!!busyAction}>
            <XCircle className="mr-1 h-3.5 w-3.5" /> Reject
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>Clear</Button>
        </div>
      )}

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : visibleItems.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <Inbox className="mx-auto mb-2 h-9 w-9 opacity-30" />
              No work items in this view.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="border-b">
                    <th className="w-10 px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        aria-label="Select visible work items"
                        checked={visibleItems.length > 0 && visibleItems.every((item) => selectedIds.includes(item.id))}
                        onChange={toggleVisibleSelection}
                      />
                    </th>
                    <th className="px-3 py-2 text-left font-medium">Module</th>
                    <th className="px-3 py-2 text-left font-medium">Work Item</th>
                    <th className="px-3 py-2 text-left font-medium">Entity</th>
                    <th className="px-3 py-2 text-left font-medium">State</th>
                    <th className="px-3 py-2 text-left font-medium">Priority</th>
                    <th className="px-3 py-2 text-left font-medium">Source</th>
                    <th className="px-3 py-2 text-left font-medium">Age</th>
                    <th className="w-10 px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((item) => {
                    const issues = asArray(item.validation_issues);
                    return (
                      <tr key={item.id} className={cn('border-b hover:bg-muted/30', selectedId === item.id && 'bg-sky-50')}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            aria-label={`Select ${workItemTitle(item)}`}
                            checked={selectedIds.includes(item.id)}
                            onChange={() => toggleSelected(item.id)}
                          />
                        </td>
                        <td className="px-3 py-2"><Badge variant="outline">{moduleFromWorkItem(item)}</Badge></td>
                        <td className="max-w-[420px] px-3 py-2">
                          <button type="button" className="text-left font-medium hover:underline" onClick={() => void loadDetail(item.id)}>
                            {workItemTitle(item)}
                          </button>
                          {issues.length > 0 && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                              <AlertTriangle className="h-3 w-3" />
                              {issues.length} issue(s)
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{item.entity_type}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={STATE_CLASS[item.state]}>{STATE_LABEL[item.state] ?? item.state}</Badge>
                        </td>
                        <td className="px-3 py-2">{item.priority}</td>
                        <td className="max-w-[180px] truncate px-3 py-2 text-xs text-muted-foreground">{item.source_type} {item.source_ref ? `· ${item.source_ref}` : ''}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{formatAge(item.created_at)}</td>
                        <td className="px-3 py-2">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => void loadDetail(item.id)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <RightDockDetailSheet
        open={!!selectedId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedId(null);
            setDetail(null);
          }
        }}
        title="Work Item Detail"
        width="xl"
      >
        {detail ? (
          <div className="flex h-[calc(100vh-120px)] flex-col gap-4 overflow-y-auto p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{moduleFromWorkItem(detail)}</Badge>
              <Badge variant="outline" className={STATE_CLASS[detail.state]}>{STATE_LABEL[detail.state]}</Badge>
              <Badge variant="outline">{detail.priority}</Badge>
              {detail.committed_entity_id && detail.entity_type && (
                <Link className="text-sm text-primary underline" to={`/e/${entityRouteSlug(detail.entity_type)}/${detail.committed_entity_id}`}>
                  Open committed entity
                </Link>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-muted-foreground">Entity</div><div className="font-mono">{detail.entity_type}</div></div>
              <div><div className="text-xs text-muted-foreground">Source</div><div>{detail.source_type}</div></div>
              <div><div className="text-xs text-muted-foreground">Source Ref</div><div className="truncate">{detail.source_ref ?? '-'}</div></div>
              <div><div className="text-xs text-muted-foreground">Updated</div><div>{new Date(detail.updated_at).toLocaleString()}</div></div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium">Proposed Payload</div>
                <Button size="sm" variant="outline" onClick={() => void runAction('save')} disabled={busyAction === 'save'}>
                  {busyAction === 'save' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                  Save
                </Button>
              </div>
              <Textarea className="min-h-[260px] font-mono text-xs" value={payloadText} onChange={(event) => setPayloadText(event.target.value)} />
            </div>

            {asArray(detail.validation_issues).length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="mb-2 text-sm font-medium text-amber-800">Validation Issues</div>
                <div className="space-y-1 text-xs text-amber-800">
                  {asArray(detail.validation_issues).map((issue, index) => (
                    <div key={index}>{text(issue.field || issue.code)}: {text(issue.message)}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-md border">
              <div className="flex items-center justify-between border-b px-3 py-2">
                <div className="text-sm font-medium">Data / Evidence Review</div>
                <div className="text-xs text-muted-foreground">AI value, source and confidence before posting</div>
              </div>
              <div className="max-h-80 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr className="border-b">
                      <th className="px-3 py-2 text-left font-medium">Field</th>
                      <th className="px-3 py-2 text-left font-medium">Value</th>
                      <th className="px-3 py-2 text-left font-medium">Evidence / Source</th>
                      <th className="px-3 py-2 text-left font-medium">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataFieldRows(detail).map((row) => (
                      <tr key={row.field} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono">{row.field}</td>
                        <td className="max-w-[220px] px-3 py-2">{row.value}</td>
                        <td className="max-w-[220px] truncate px-3 py-2 text-muted-foreground">{row.source}</td>
                        <td className="px-3 py-2">{row.confidence}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-md border p-3">
              <div className="mb-2 text-sm font-medium">Evidence</div>
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">{JSON.stringify(asRecord(detail.evidence), null, 2)}</pre>
            </div>

            <div className="sticky bottom-0 mt-auto flex flex-wrap gap-2 border-t bg-background pt-3">
              {detail.state === 'draft' && (
                <Button size="sm" variant="outline" onClick={() => void runAction('submit')} disabled={!!busyAction}>
                  <Send className="mr-1 h-3.5 w-3.5" /> Submit
                </Button>
              )}
              {detail.state === 'pending_review' && (
                <Button size="sm" onClick={() => void runAction('approve')} disabled={!!busyAction}>
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Approve
                </Button>
              )}
              {detail.state === 'approved' && (
                <Button size="sm" onClick={() => void runAction('commit')} disabled={!!busyAction}>
                  <ClipboardCheck className="mr-1 h-3.5 w-3.5" /> Post
                </Button>
              )}
              {['draft', 'pending_review', 'approved'].includes(detail.state) && (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => void runAction('advance')} disabled={!!busyAction}>
                  {busyAction === 'advance' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
                  Review + Post
                </Button>
              )}
              {['draft', 'pending_review'].includes(detail.state) && (
                <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => void runAction('reject')} disabled={!!busyAction}>
                  <XCircle className="mr-1 h-3.5 w-3.5" /> Reject
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        )}
      </RightDockDetailSheet>
    </div>
  );
}
